import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { FaLink, FaSpinner, FaHistory, FaExclamationCircle } from "react-icons/fa";
import SourceCard from "@/components/SourceCard";
import { analyzeSource } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { AlertCircle, Shield, HistoryIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "../contexts/AuthContext";
import { getUserAnalysisHistory, AnalysisRecord } from "../services/analysisHistoryService";

// Form validation schema
const sourceFormSchema = z.object({
  url: z.string()
    .min(3, { message: "URL must be at least 3 characters." })
    .refine(val => val.includes('.'), { message: "Must include a domain extension (e.g., .com)" }),
  category: z.enum(["news", "social", "academic", "blog", "other"], {
    required_error: "Please select a category.",
  }),
});

const Sources = () => {
  const [url, setUrl] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<{
    url: string;
    credibility: number;
    summary: string;
    factors: { name: string; score: number; explanation: string }[];
  } | null>(null);
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("url");

  // Firestore Analysis History State
  const { currentUser } = useAuth();
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Advanced form
  const form = useForm<z.infer<typeof sourceFormSchema>>({
    resolver: zodResolver(sourceFormSchema),
    defaultValues: {
      url: "",
      category: "news",
    },
  });

  const processUrl = (inputUrl: string): string => {
    let processedUrl = inputUrl.trim();
    if (!processedUrl.startsWith('http://') && !processedUrl.startsWith('https://')) {
      processedUrl = 'https://' + processedUrl;
    }
    return processedUrl;
  };

  const handleSourceAnalysis = async () => {
    if (!url.trim()) {
      toast({
        title: "Error",
        description: "Please enter a URL to analyze",
        variant: "destructive",
      });
      return;
    }

    const processedUrl = processUrl(url);
    await analyzeSourceCredibility(processedUrl);
  };

  const analyzeSourceCredibility = async (processedUrl: string) => {
    setIsAnalyzing(true);
    try {
      const result = await analyzeSource(processedUrl);
      const analysisData = {
        url: processedUrl,
        ...result
      };
      
      setAnalysisResult(analysisData);
      
      toast({
        title: "Analysis Complete",
        description: `Credibility score: ${result.credibility}%`,
      });
    } catch (error) {
      console.error("Analysis error:", error);
      toast({
        title: "Analysis Failed",
        description: "Could not analyze source. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const onAdvancedFormSubmit = async (values: z.infer<typeof sourceFormSchema>) => {
    const processedUrl = processUrl(values.url);
    await analyzeSourceCredibility(processedUrl);
  };

  // Fetch analysis history from Firestore
  useEffect(() => {
    if (currentUser) {
      setHistoryLoading(true);
      getUserAnalysisHistory()
        .then(history => {
          setAnalysisHistory(history);
          setHistoryError(null);
        })
        .catch(err => {
          console.error("Error fetching analysis history:", err);
          setHistoryError("Failed to load analysis history.");
        })
        .finally(() => {
          setHistoryLoading(false);
        });
    } else {
      setAnalysisHistory([]);
      setHistoryLoading(false);
    }
  }, [currentUser]);

  const sampleSources = [
    {
      url: 'https://reuters.com',
      credibility: 92,
      summary: 'Reuters is a highly credible international news agency with a strong reputation for factual reporting and objectivity.',
      factors: [
        { name: 'Factual Reporting', score: 95, explanation: 'Consistent record of accurate reporting with minimal errors.' },
        { name: 'Transparency', score: 90, explanation: 'Clear ownership structure and funding information available.' },
        { name: 'Editorial Standards', score: 94, explanation: 'Robust editorial policies with emphasis on neutrality and fact-checking.' },
        { name: 'Bias', score: 88, explanation: 'Minimal bias with focus on factual reporting rather than opinion.' }
      ]
    },
    {
      url: 'https://mediabiasfactcheck.com',
      credibility: 83,
      summary: 'Media Bias/Fact Check is generally reliable for media bias analysis, though its methodology has some limitations.',
      factors: [
        { name: 'Factual Reporting', score: 80, explanation: 'Generally accurate information with occasional oversimplifications.' },
        { name: 'Transparency', score: 85, explanation: 'Clear information about methodology and ownership.' },
        { name: 'Editorial Standards', score: 78, explanation: 'Consistent application of bias criteria, though some subjective elements exist.' },
        { name: 'Bias', score: 75, explanation: 'Generally neutral with slight liberal bias in some assessments.' }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-6 px-2 md:px-8 lg:px-16">
      <div className="w-full max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl space-y-8 px-2 md:px-8 lg:px-12">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Source Credibility</h1>
          <p className="mt-2 text-gray-400">Check and verify credibility of news sources</p>
        </div>

        <Tabs defaultValue="url" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="url">Quick Analysis</TabsTrigger>
            <TabsTrigger value="advanced">Advanced Options</TabsTrigger>
          </TabsList>
          <TabsContent value="url" className="mt-4">
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle>Quick Source Analysis</CardTitle>
                <CardDescription>
                  Enter a URL to analyze the credibility of a news source or website.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="source-url" className="sr-only">
                      Website URL
                    </Label>
                    <div className="relative">
                      <FaLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        id="source-url"
                        placeholder="Enter website URL (e.g., bbc.com)"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={handleSourceAnalysis} 
                    disabled={isAnalyzing || !url.trim()} 
                    className="whitespace-nowrap"
                  >
                    {isAnalyzing ? (
                      <>
                        <FaSpinner className="h-4 w-4 mr-2 animate-spin" /> Analyzing...
                      </>
                    ) : (
                      "Analyze Source"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Our AI evaluates sources based on historical accuracy, transparency, editorial practices, and known biases.
                </p>
                <p className="text-xs text-gray-400 mt-2 italic text-center">
                  Note: This tool analyzes public web pages and websites. It does not currently support direct analysis of social media platforms like Facebook or Twitter.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="advanced" className="mt-4">
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle>Advanced Analysis Options</CardTitle>
                <CardDescription>
                  Customize your source analysis with additional parameters.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onAdvancedFormSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Source URL</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <FaLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                              <Input
                                placeholder="Enter website URL (e.g., bbc.com)"
                                className="pl-10"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Source Category</FormLabel>
                          <FormControl>
                            <select
                              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              {...field}
                            >
                              <option value="news">News Organization</option>
                              <option value="social">Social Media</option>
                              <option value="academic">Academic/Research</option>
                              <option value="blog">Blog/Opinion</option>
                              <option value="other">Other</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Alert className="bg-black/30 border-truth/30">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Analysis Depth</AlertTitle>
                      <AlertDescription>
                        Advanced analysis evaluates additional factors including ownership history, citation practices, 
                        and historical accuracy trends.
                      </AlertDescription>
                    </Alert>

                    <Button 
                      type="submit" 
                      className="w-full md:w-auto"
                      disabled={isAnalyzing}
                    >
                      {isAnalyzing ? (
                        <>
                          <FaSpinner className="h-4 w-4 mr-2 animate-spin" /> Analyzing...
                        </>
                      ) : (
                        <><Shield className="mr-2 h-4 w-4" /> Run In-Depth Analysis</>
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {analysisResult && (
          <div className="animate-fade-in">
            <h2 className="text-2xl font-bold mb-4">Analysis Results</h2>
            <SourceCard 
              url={analysisResult.url}
              credibility={analysisResult.credibility}
              summary={analysisResult.summary}
              factors={analysisResult.factors}
            />
          </div>
        )}

        {/* Display User's Analysis History from Firestore */}
        <div className="space-y-4 mt-8">
          <div className="flex items-center gap-2">
            <HistoryIcon className="text-truth h-5 w-5" />
            <h2 className="text-xl font-bold">My Analysis History</h2>
          </div>
          {historyLoading && (
            <div className="flex justify-center items-center p-8">
              <FaSpinner className="h-8 w-8 animate-spin text-truth" />
              <p className="ml-3 text-gray-400">Loading history...</p>
            </div>
          )}
          {!historyLoading && historyError && (
            <Alert variant="destructive">
              <FaExclamationCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{historyError}</AlertDescription>
            </Alert>
          )}
          {!historyLoading && !historyError && analysisHistory.length === 0 && (
            <p className="text-gray-400 italic">No analysis history found. Analyses performed in the 'Tools' section will appear here.</p>
          )}
          {!historyLoading && !historyError && analysisHistory.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {analysisHistory.map((record) => (
                <Card key={record.id} className="bg-black/30 border-gray-700 hover:border-truth/50 transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-truth text-lg">{record.analysisType}</CardTitle>
                    <CardDescription className="text-xs text-gray-500">
                      {new Date(record.timestamp as any).toLocaleString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div>
                      <strong className="text-gray-300">Input:</strong> 
                      <p className="text-gray-400 break-all">{record.inputSummary}</p>
                    </div>
                    {record.resultSummary && (
                      <div>
                        <strong className="text-gray-300">Result:</strong> 
                        <p className="text-gray-400 break-all">{record.resultSummary}</p>
                      </div>
                    )}
                    {record.parameters && Object.keys(record.parameters).length > 0 && (
                       <div>
                        <strong className="text-gray-300">Parameters:</strong> 
                        <ul className="list-disc list-inside pl-4 text-gray-400">
                          {Object.entries(record.parameters).map(([key, value]) => (
                            <li key={key}>{`${key}: ${String(value)}`}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold mt-8 mb-4">Sample Analyzed Sources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sampleSources.map((source, index) => (
              <SourceCard
                key={`sample-${index}`}
                url={source.url}
                credibility={source.credibility}
                summary={source.summary}
                factors={source.factors}
              />
            ))}
          </div>
        </div>

        <div className="mt-8 bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Understanding Source Credibility</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h3 className="font-semibold text-truth mb-2">What We Analyze</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li>Historical accuracy of reporting</li>
                <li>Transparency about ownership and funding</li>
                <li>Editorial policies and standards</li>
                <li>Citation practices and sources used</li>
                <li>Known biases and agenda</li>
                <li>Corrections policy and implementation</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-truth mb-2">Credibility Score Guide</h3>
              <ul className="space-y-2 text-gray-300">
                <li><span className="inline-block w-4 h-4 bg-green-500 rounded-full mr-2"></span>80-100: High credibility</li>
                <li><span className="inline-block w-4 h-4 bg-yellow-500 rounded-full mr-2"></span>60-79: Mostly reliable</li>
                <li><span className="inline-block w-4 h-4 bg-orange-500 rounded-full mr-2"></span>40-59: Mixed reliability</li>
                <li><span className="inline-block w-4 h-4 bg-red-500 rounded-full mr-2"></span>0-39: Low credibility</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sources;
